// Framework-agnostic helpers shared by admin and landing.

/**
 * `03.09.2026`. Built by hand rather than via `Intl`: browsers with no `uz`
 * date patterns fall back to the root locale and render `2026 M09 3`.
 */
export function formatDate(input: string | Date): string {
  const date = typeof input === "string" ? new Date(input) : input
  if (Number.isNaN(date.getTime()))
    return ""

  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  return `${day}.${month}.${date.getFullYear()}`
}

/**
 * `+998 94 329 69 49`. Numbers arrive from the API unspaced. Anything that is
 * not a 12-digit Uzbek number is returned untouched rather than mangled, and
 * formatting an already-formatted number is a no-op.
 */
export function formatPhone(input: string): string {
  const digits = input.replace(/\D/g, "")
  if (!/^998\d{9}$/.test(digits))
    return input

  const rest = digits.slice(3)
  return `+998 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 7)} ${rest.slice(7)}`
}

export function formatMoney(amount: number, currency = "UZS", locale = "uz-UZ"): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount)
}

/**
 * `2.4 MB`. Binary units, since the sizes shown come from upload limits that
 * are themselves powers of two. One decimal below 10 and none above, so a
 * column of sizes stays the same width.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0)
    return "0 B"

  const units = ["B", "KB", "MB", "GB"]
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent

  return `${value < 10 && exponent > 0 ? value.toFixed(1) : Math.round(value)} ${units[exponent]}`
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}
